/**
 * DebtsPage — Phase 1: manual entry + payments timeline.
 * Owner-friendly list of debts with summary cards, filters, and a slide-out
 * drawer for adding payments and viewing the chronological history.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertCircle, AlertTriangle, Calendar, Plus, Trash2, X, Receipt,
  Wallet, ChevronRight, Building2, Banknote,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEBT_TYPES = [
  { value: 'supplier', label: 'Supplier' },
  { value: 'hmrc', label: 'HMRC / Tax' },
  { value: 'loan', label: 'Loan' },
  { value: 'rent', label: 'Rent' },
  { value: 'utility', label: 'Utility' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
];
const PRIORITIES = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];
const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'cleared', label: 'Cleared' },
  { value: 'written_off', label: 'Written off' },
];

const priorityColor = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-amber-100 text-amber-700 border-amber-200',
  normal: 'bg-blue-100 text-blue-700 border-blue-200',
  low: 'bg-gray-100 text-gray-700 border-gray-200',
};
const statusColor = {
  active: 'text-foreground',
  on_hold: 'text-muted-foreground italic',
  cleared: 'text-emerald-600',
  written_off: 'text-muted-foreground line-through',
};

const fmt = (n) => `£${(n ?? 0).toFixed(2)}`;

export default function DebtsPage() {
  const { token } = useAuth();
  const [debts, setDebts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [typeFilter, setTypeFilter] = useState('all');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [drawerId, setDrawerId] = useState(null);

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const reload = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (typeFilter !== 'all') params.set('debt_type', typeFilter);
      if (overdueOnly) params.set('overdue_only', 'true');
      const [listRes, sumRes] = await Promise.all([
        axios.get(`${API}/debts?${params}`, auth),
        axios.get(`${API}/debts/summary`, auth),
      ]);
      setDebts(listRes.data.debts || []);
      setSummary(sumRes.data);
    } catch (e) {
      toast.error('Failed to load debts');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [statusFilter, typeFilter, overdueOnly]);

  return (
    <div className="space-y-6" data-testid="debts-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground mb-1">
            <AlertCircle className="w-3.5 h-3.5" /> Debt Management · Phase 1
          </div>
          <h1 className="text-3xl font-semibold">Debts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track money owed to suppliers, HMRC, landlords & loans. Add a payment whenever you pay something down.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-debt-btn">
              <Plus className="w-4 h-4 mr-2" /> New debt
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>New debt</DialogTitle>
              <DialogDescription>Record money you owe to a supplier, HMRC, landlord or other creditor.</DialogDescription>
            </DialogHeader>
            <DebtForm
              onClose={() => setCreateOpen(false)}
              onSaved={() => { setCreateOpen(false); reload(); }}
              auth={auth}
              hideTitle
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard
            icon={Wallet}
            label="Total owed (active)"
            value={fmt(summary.total_owed)}
            sub={`${summary.active_count} active debt${summary.active_count === 1 ? '' : 's'}`}
            tone="default"
            data-testid="summary-total-owed"
          />
          <SummaryCard
            icon={AlertTriangle}
            label="Overdue"
            value={fmt(summary.overdue_total)}
            sub={`${summary.overdue_count} overdue`}
            tone={summary.overdue_total > 0 ? 'danger' : 'default'}
            data-testid="summary-overdue"
          />
          <SummaryCard
            icon={Calendar}
            label="Due in 7 days"
            value={fmt(summary.due_within_7_days)}
            sub="Next week"
            tone={summary.due_within_7_days > 0 ? 'warn' : 'default'}
            data-testid="summary-due-7"
          />
          <SummaryCard
            icon={Calendar}
            label="Due in 30 days"
            value={fmt(summary.due_within_30_days)}
            sub="Next month"
            tone="default"
            data-testid="summary-due-30"
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <FilterSelect value={statusFilter} onChange={setStatusFilter} placeholder="Status" options={[{ value: 'all', label: 'All statuses' }, ...STATUSES]} testid="filter-status" />
        <FilterSelect value={typeFilter} onChange={setTypeFilter} placeholder="Type" options={[{ value: 'all', label: 'All types' }, ...DEBT_TYPES]} testid="filter-type" />
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={overdueOnly}
            onChange={(e) => setOverdueOnly(e.target.checked)}
            data-testid="filter-overdue"
          />
          Overdue only
        </label>
      </div>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-muted-foreground text-sm">Loading debts…</div>
          ) : debts.length === 0 ? (
            <div className="p-12 text-center" data-testid="debts-empty">
              <Building2 className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">No debts match your filters yet.</p>
              <p className="text-xs text-muted-foreground mt-1">Click <strong>New debt</strong> to add your first one.</p>
            </div>
          ) : (
            <div className="divide-y">
              {debts.map((d) => (
                <button
                  key={d.id}
                  className="w-full text-left p-4 hover:bg-muted/40 transition-colors flex items-center gap-4"
                  onClick={() => setDrawerId(d.id)}
                  data-testid={`debt-row-${d.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-medium ${statusColor[d.effective_status] || ''}`}>{d.creditor}</span>
                      <Badge variant="outline" className={`text-[10px] ${priorityColor[d.priority] || ''}`}>{d.priority}</Badge>
                      <Badge variant="outline" className="text-[10px] capitalize">{d.debt_type}</Badge>
                      {d.overdue && (
                        <Badge className="text-[10px] bg-red-500 hover:bg-red-500">Overdue</Badge>
                      )}
                      {d.effective_status === 'cleared' && (
                        <Badge className="text-[10px] bg-emerald-500 hover:bg-emerald-500">Cleared</Badge>
                      )}
                      {d.needs_review && (
                        <Badge className="text-[10px] bg-amber-500 hover:bg-amber-500" data-testid={`needs-review-badge-${d.id}`}>
                          Needs review
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      {d.due_date && (
                        <span>
                          Due {new Date(d.due_date).toLocaleDateString()} ({d.days_to_due >= 0 ? `in ${d.days_to_due}d` : `${Math.abs(d.days_to_due)}d ago`})
                        </span>
                      )}
                      {d.payment_count > 0 && <span>· {d.payment_count} payment{d.payment_count === 1 ? '' : 's'}</span>}
                      {d.notes && <span className="italic">· {d.notes}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-mono font-semibold ${d.balance > 0 ? 'text-foreground' : 'text-emerald-600'}`}>{fmt(d.balance)}</div>
                    {d.amount_paid > 0 && d.balance > 0 && (
                      <div className="text-[11px] text-muted-foreground">of {fmt(d.amount_owed)}</div>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drawer for debt detail + payments */}
      {drawerId && (
        <DebtDrawer
          debtId={drawerId}
          onClose={() => setDrawerId(null)}
          onChanged={reload}
          auth={auth}
        />
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

const SummaryCard = ({ icon: Icon, label, value, sub, tone = 'default', ...rest }) => {
  const toneCls = {
    default: 'border-border',
    danger: 'border-red-200 bg-red-50/40',
    warn: 'border-amber-200 bg-amber-50/40',
  };
  return (
    <Card className={toneCls[tone] || ''} {...rest}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
          <Icon className="w-3.5 h-3.5" /> {label}
        </div>
        <div className="text-2xl font-semibold font-mono">{value}</div>
        <div className="text-xs text-muted-foreground mt-1">{sub}</div>
      </CardContent>
    </Card>
  );
};

const FilterSelect = ({ value, onChange, options, placeholder, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-44" data-testid={testid}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const DebtForm = ({ onClose, onSaved, auth, initial = null, hideTitle = false }) => {
  const [form, setForm] = useState(initial || {
    creditor: '', amount_owed: '', debt_type: 'supplier',
    due_date: '', priority: 'normal', status: 'active',
    interest_rate: '', payment_terms: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.creditor.trim() || !form.amount_owed) {
      toast.error('Creditor and amount are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount_owed: parseFloat(form.amount_owed),
        interest_rate: form.interest_rate === '' ? null : parseFloat(form.interest_rate),
        due_date: form.due_date || null,
      };
      if (initial?.id) {
        await axios.put(`${API}/debts/${initial.id}`, payload, auth);
      } else {
        await axios.post(`${API}/debts`, payload, auth);
      }
      toast.success(initial ? 'Debt updated' : 'Debt added');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {!hideTitle && (
        <div className="pb-3">
          <h2 className="text-lg font-semibold" data-testid="debt-form-title">
            {initial ? 'Edit debt' : 'New debt'}
          </h2>
        </div>
      )}
      <div className="space-y-3 py-2">
        <div>
          <Label>Creditor *</Label>
          <Input
            value={form.creditor}
            onChange={(e) => setForm({ ...form, creditor: e.target.value })}
            placeholder="e.g. Mission Coffee, HMRC, Landlord"
            data-testid="debt-creditor"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Amount owed *</Label>
            <Input
              type="number" step="0.01" min="0"
              value={form.amount_owed}
              onChange={(e) => setForm({ ...form, amount_owed: e.target.value })}
              placeholder="0.00"
              data-testid="debt-amount"
            />
          </div>
          <div>
            <Label>Due date</Label>
            <Input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              data-testid="debt-due-date"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Type</Label>
            <Select value={form.debt_type} onValueChange={(v) => setForm({ ...form, debt_type: v })}>
              <SelectTrigger data-testid="debt-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DEBT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="debt-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Interest rate (% APR)</Label>
            <Input
              type="number" step="0.1"
              value={form.interest_rate}
              onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
              placeholder="optional"
            />
          </div>
          <div>
            <Label>Payment terms</Label>
            <Input
              value={form.payment_terms}
              onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
              placeholder="e.g. Net 30"
            />
          </div>
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
            placeholder="Optional context"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={saving} data-testid="save-debt-btn">
          {saving ? 'Saving…' : (initial ? 'Save changes' : 'Create debt')}
        </Button>
      </div>
    </>
  );
};

const DebtDrawer = ({ debtId, onClose, onChanged, auth }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState('bank');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/debts/${debtId}`, auth);
      setData(res.data);
    } catch {
      toast.error('Failed to load debt');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [debtId]);

  const addPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Enter a payment amount');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/debts/${debtId}/payments`, {
        amount: parseFloat(paymentAmount),
        date: paymentDate,
        method: paymentMethod,
        notes: paymentNotes || null,
      }, auth);
      toast.success('Payment recorded');
      setPaymentAmount(''); setPaymentNotes('');
      await load();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const deletePayment = async (paymentId) => {
    if (!window.confirm('Remove this payment? The debt balance will go back up by that amount.')) return;
    try {
      await axios.delete(`${API}/debts/payments/${paymentId}`, auth);
      toast.success('Payment removed');
      await load();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };

  const deleteDebt = async () => {
    if (!window.confirm('Delete this debt and all its payments? Cannot be undone.')) return;
    try {
      await axios.delete(`${API}/debts/${debtId}`, auth);
      toast.success('Debt deleted');
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading || !data) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/60">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  const debt = data.debt;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="debt-drawer">
      <div className="flex-1 bg-background/60" onClick={onClose} />
      <div className="w-full max-w-xl bg-background border-l shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-background border-b px-6 py-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground">
              {debt.debt_type} · {debt.priority} priority
              {debt.needs_review && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200 text-[10px] font-medium" data-testid="drawer-needs-review-badge">
                  Needs review
                </span>
              )}
            </div>
            <h2 className="text-xl font-semibold truncate">{debt.creditor}</h2>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="drawer-close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {editing ? (
            <DebtForm
              initial={debt}
              auth={auth}
              onClose={() => setEditing(false)}
              onSaved={() => { setEditing(false); load(); onChanged(); }}
            />
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <SummaryCard icon={Wallet} label="Original" value={fmt(debt.amount_owed)} sub="Amount owed" />
                <SummaryCard icon={Banknote} label="Paid" value={fmt(debt.amount_paid)} sub={`${debt.payment_count} payment${debt.payment_count === 1 ? '' : 's'}`} />
                <SummaryCard
                  icon={AlertCircle}
                  label="Balance"
                  value={fmt(debt.balance)}
                  sub={debt.effective_status}
                  tone={debt.overdue ? 'danger' : (debt.balance <= 0 ? 'default' : 'warn')}
                />
              </div>

              <div className="space-y-1 text-sm">
                {debt.due_date && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Due date</span>
                    <span>{new Date(debt.due_date).toLocaleDateString()} ({debt.days_to_due >= 0 ? `in ${debt.days_to_due}d` : `${Math.abs(debt.days_to_due)}d ago`})</span>
                  </div>
                )}
                {debt.interest_rate != null && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Interest</span>
                    <span>{debt.interest_rate}% APR</span>
                  </div>
                )}
                {debt.payment_terms && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Terms</span>
                    <span>{debt.payment_terms}</span>
                  </div>
                )}
                {debt.notes && (
                  <div className="pt-2">
                    <div className="text-xs text-muted-foreground mb-1">Notes</div>
                    <div className="bg-muted/40 rounded p-2 text-sm italic">{debt.notes}</div>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-debt-btn">
                  Edit
                </Button>
                <Button variant="outline" size="sm" onClick={deleteDebt} className="text-destructive" data-testid="delete-debt-btn">
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                </Button>
              </div>

              {/* Add payment */}
              {debt.balance > 0 && debt.effective_status === 'active' && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Record a payment</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Amount</Label>
                        <Input
                          type="number" step="0.01" min="0.01"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          placeholder={fmt(debt.balance).slice(1)}
                          data-testid="payment-amount"
                        />
                      </div>
                      <div>
                        <Label>Date</Label>
                        <Input
                          type="date"
                          value={paymentDate}
                          onChange={(e) => setPaymentDate(e.target.value)}
                          data-testid="payment-date"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Method</Label>
                      <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                        <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bank">Bank transfer</SelectItem>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="card">Card</SelectItem>
                          <SelectItem value="direct_debit">Direct debit</SelectItem>
                          <SelectItem value="standing_order">Standing order</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Input
                        value={paymentNotes}
                        onChange={(e) => setPaymentNotes(e.target.value)}
                        placeholder="Optional"
                      />
                    </div>
                    <div className="flex gap-2 justify-between items-center">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setPaymentAmount(debt.balance.toFixed(2))}
                      >
                        Pay full balance ({fmt(debt.balance)})
                      </Button>
                      <Button onClick={addPayment} disabled={submitting} data-testid="record-payment-btn">
                        <Receipt className="w-4 h-4 mr-1.5" />
                        {submitting ? 'Saving…' : 'Record payment'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Payments history */}
              <div>
                <h3 className="text-sm font-medium mb-2">Payments timeline</h3>
                {data.payments.length === 0 ? (
                  <div className="text-sm text-muted-foreground p-4 text-center border border-dashed rounded-md">
                    No payments recorded yet.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {data.payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between p-3 border rounded-md text-sm" data-testid={`payment-row-${p.id}`}>
                        <div>
                          <div className="font-medium font-mono">{fmt(p.amount)}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(p.date).toLocaleDateString()} · {p.method}
                            {p.notes && ` · ${p.notes}`}
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => deletePayment(p.id)} className="text-destructive">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
